// ONE status colour system (docs/specs/DESIGN-SYSTEM.md §1b).
//
//  1. A tone means the same thing on every object.
//  2. Two states of one object never share a tone (the one exception: closed
//     reject-bin outcomes all use `gone`; their words say where the bin went).
//  3. A status is a SOFT pill: tint + coloured text + a leading dot + its words.
//
// Render a status with <StatusPill object status/> (src/components/ui). Use
// `statusToneClasses(tone)` only where a pill does not fit (a legend dot, a
// coloured cell edge) so those read from the same tones.
import { enumWords } from '@/lib/enumWords'
import { DELIVERY_STATUS_LABELS } from '@/lib/deliveryColumns'

export type Tone =
  | 'draft' | 'progress' | 'stored' | 'staging' | 'treatment'
  | 'exception' | 'good' | 'attention' | 'bad' | 'gone'

export const TONES: readonly Tone[] = [
  'draft', 'progress', 'stored', 'staging', 'treatment',
  'exception', 'good', 'attention', 'bad', 'gone',
]

export type StatusObject =
  | 'pallet' | 'run' | 'mission' | 'reject_note' | 'reject_bin' | 'bin'
  | 'compliance_item' | 'certification' | 'equipment' | 'training' | 'work_order'
  | 'delivery' | 'ppecb_consignment' | 'truck_consignment' | 'count_session' | 'stock_level' | 'security_site' | 'pack_window' | 'harvest_client' | 'cooling_batch'
  | 'asset' | 'procedure_item' | 'supplier_request' | 'supplier_delivery' | 'agent_visit'
  | 'bin_loan_age' | 'bin_loan' | 'bin_holding' | 'tenant' | 'non_conformance' | 'receipt_line' | 'ecert_trade_unit' | 'setup_check'
  | 'irrigation_delivery'

type Entry = { tone: Tone; label: string }

export const STATUS_TONES: Record<StatusObject, Record<string, Entry>> = {
  // A fruit delivery (deliveries.status), UX-laws U14 UF-01. ONE map for the
  // board card and the delivery page; seven states, seven tones.
  delivery: {
    DRAFT:             { tone: 'draft',     label: 'Draft (WhatsApp)' },
    RECEIVED:          { tone: 'progress',  label: DELIVERY_STATUS_LABELS.RECEIVED },
    AWAITING_DRENCH:   { tone: 'staging',   label: DELIVERY_STATUS_LABELS.AWAITING_DRENCH },
    DRENCHED:          { tone: 'treatment', label: DELIVERY_STATUS_LABELS.DRENCHED },
    OUT_FOR_COLOUR_UP: { tone: 'exception', label: DELIVERY_STATUS_LABELS.OUT_FOR_COLOUR_UP },
    IN_STOCK:          { tone: 'stored',    label: DELIVERY_STATUS_LABELS.IN_STOCK },
    TIPPED:            { tone: 'gone',      label: DELIVERY_STATUS_LABELS.TIPPED },
  },
  // A PPECB consignment (ppecb_consignments.inspection_status), UX-laws U16
  // UF-08. The seven states the edge functions and RPCs write; VALIDATED is
  // never written. FAILED = PPECB refused it: fix it, so it stays the amber one.
  ppecb_consignment: {
    DRAFT:      { tone: 'draft',     label: 'Draft' },
    SUBMITTING: { tone: 'progress',  label: 'Submitting' },
    SUBMITTED:  { tone: 'staging',   label: 'Submitted' },
    FAILED:     { tone: 'attention', label: 'Failed' },
    INSPECTED:  { tone: 'good',      label: 'Inspected' },
    REJECTED:   { tone: 'bad',       label: 'Rejected' },
    RECALLED:   { tone: 'gone',      label: 'Recalled' },
  },
  // One day's planned-vs-delivered irrigation verdict
  // (src/lib/irrigation/deliveredVsPlanned.ts DeliveryVerdict). `no_programme`
  // is not a state of the delivery and is words on the page, not a pill.
  irrigation_delivery: {
    ok:          { tone: 'good',      label: 'Delivered' },
    under:       { tone: 'bad',       label: 'Under' },
    cut_short:   { tone: 'exception', label: 'Cut short' },
    over:        { tone: 'attention', label: 'Over' },
    no_runs:     { tone: 'draft',     label: 'No runs logged' },
    in_progress: { tone: 'progress',  label: 'Still running' },
  },
  // A truck consignment (truck_consignments.status), UX-laws U20 UF-07.
  truck_consignment: {
    OPEN:      { tone: 'draft',   label: 'Open' },
    READY:     { tone: 'staging', label: 'Ready' },
    LOADED:    { tone: 'good',    label: 'Loaded' },
    CANCELLED: { tone: 'bad',     label: 'Cancelled' },
  },
  // A stock count session (stock_count_sessions.status), UX-laws U21 UF-04.
  count_session: {
    OPEN:      { tone: 'progress', label: 'Open' },
    SUBMITTED: { tone: 'staging',  label: 'Submitted' },
    POSTED:    { tone: 'good',     label: 'Posted' },
    CANCELLED: { tone: 'gone',     label: 'Cancelled' },
  },
  // A stock item's level, UX-laws U21 UF-04. Healthy has no entry: it shows nothing.
  stock_level: {
    LOW:        { tone: 'attention', label: 'Low' },
    BELOW_ZERO: { tone: 'bad',       label: 'Below zero' },
  },
  // How long a bin loan has been out, UX-laws U23 UF-01. A loan under 30 days
  // shows no pill; the days figure beside it stays.
  bin_loan_age: {
    OVER_60: { tone: 'bad',       label: 'Over 60 days' },
    OVER_30: { tone: 'attention', label: 'Over 30 days' },
  },
  // A bin loan (bin_loans.status) and a grower-bin holding (grower_bin_holdings.status),
  // UX-laws U23 UF-02. One pill on every tab; three tones, never shared by two states.
  bin_loan: {
    OPEN:      { tone: 'attention', label: 'Open' },
    CLOSED:    { tone: 'good',      label: 'Closed' },
    CANCELLED: { tone: 'gone',      label: 'Cancelled' },
  },
  bin_holding: {
    RETURNED:  { tone: 'good', label: 'Returned' },
    CANCELLED: { tone: 'gone', label: 'Cancelled' },
  },
  pallet: {
    IN_PRODUCTION:      { tone: 'progress',  label: 'In Production' },
    IN_COLDROOM:        { tone: 'stored',    label: 'In Coldroom' },
    IN_LOADING_AREA:    { tone: 'staging',   label: 'Loading Area' },
    INSPECTED_APPROVED: { tone: 'good',      label: 'Approved' },
    INSPECTED_REJECTED: { tone: 'bad',       label: 'Rejected' },
    DISPATCHED:         { tone: 'gone',      label: 'Dispatched' },
    RETURNED_FRESHMARK: { tone: 'exception', label: 'Returned (Freshmark)' },
  },
  run: {
    ACTIVE:           { tone: 'progress',  label: 'Active' },
    TIPPING_COMPLETE: { tone: 'attention', label: 'Tipping stopped' },
    COMPLETE:         { tone: 'gone',      label: 'Complete' },
    // Derived, not a DB status: a run with verified_at wears this BESIDE Complete (U17 UF-02).
    VERIFIED:         { tone: 'good',      label: 'Verified' },
    ARCHIVED:         { tone: 'draft',     label: 'Archived' },
  },
  mission: {
    DRAFT:      { tone: 'draft',    label: 'Draft' },
    LOADING:    { tone: 'progress', label: 'Loading' },
    READY:      { tone: 'stored',   label: 'Ready' },
    DISPATCHED: { tone: 'gone',     label: 'Dispatched' },
    CANCELLED:  { tone: 'bad',      label: 'Cancelled' },
  },
  reject_note: {
    DRAFT:      { tone: 'draft', label: 'Draft' },
    DISPATCHED: { tone: 'gone',  label: 'Dispatched' },
    RECEIVED:   { tone: 'good',  label: 'Received' },
    CANCELLED:  { tone: 'bad',   label: 'Cancelled' },
  },
  reject_bin: {
    OPEN:                { tone: 'progress',  label: 'Open · filling' },
    READY:               { tone: 'attention', label: 'Ready · undecided' },
    AT_STALL:            { tone: 'stored',    label: 'At stall' },
    TRANSFERRED:         { tone: 'gone',      label: 'Transferred to cage' },
    SENT_TO_JUICE:       { tone: 'gone',      label: 'Sent to juice' },
    SENT_TO_ANIMAL_FEED: { tone: 'gone',      label: 'Sent to animal feed' },
    SENT_TO_COMPOST:     { tone: 'gone',      label: 'Sent to compost' },
    INTERNAL_USED:       { tone: 'gone',      label: 'Internal use' },
    COLLECTED:           { tone: 'gone',      label: 'Collected by farmer' },
    DISPOSED:            { tone: 'gone',      label: 'Disposed' },
  },
  // The inventory-bin record (BinDetail). Only statuses that render live here:
  // a second vocabulary in one map would let two states share a tone (rule 2).
  bin: {
    REBINNED:    { tone: 'staging', label: 'Awaiting placement' },
    IN_COLDROOM: { tone: 'stored',  label: 'In coldroom' },
    TIPPED:      { tone: 'gone',    label: 'Tipped (consumed)' },
    CANCELLED:   { tone: 'bad',     label: 'Cancelled' },
  },
  // Pack Schedule windows. The database holds only PLANNED and CANCELLED; NEXT,
  // RUNNING and ENDED are derived by the page from the clock and the linked run.
  // "Past its slot" is plain text, not a state, so it is not here.
  pack_window: {
    PLANNED:   { tone: 'draft',    label: 'Planned' },
    NEXT:      { tone: 'staging',  label: 'Next' },
    RUNNING:   { tone: 'progress', label: 'Running' },
    ENDED:     { tone: 'gone',     label: 'Ended' },
    CANCELLED: { tone: 'bad',      label: 'Cancelled' },
  },
  // The calendar's due rows (UX-laws U02 R1). Only the statuses the calendar
  // engine can return for a due row are listed, plus their siblings.
  compliance_item: {
    ACTIVE:  { tone: 'progress', label: 'Active' },
    RENEWED: { tone: 'good',     label: 'Renewed' },
    WAIVED:  { tone: 'draft',    label: 'Waived' },
    EXPIRED: { tone: 'bad',      label: 'Expired' },
  },
  certification: {
    DRAFT:      { tone: 'draft',    label: 'Draft' },
    ACTIVE:     { tone: 'progress', label: 'Active' },
    SUPERSEDED: { tone: 'gone',     label: 'Superseded' },
    WITHDRAWN:  { tone: 'bad',      label: 'Withdrawn' },
  },
  equipment: {
    ACTIVE:         { tone: 'progress', label: 'In service' },
    OUT_OF_SERVICE: { tone: 'bad',      label: 'Out of service' },
    RETIRED:        { tone: 'gone',     label: 'Retired' },
  },
  training: {
    PASS:        { tone: 'good',      label: 'Passed' },
    CONDITIONAL: { tone: 'attention', label: 'Conditional' },
    FAIL:        { tone: 'bad',       label: 'Failed' },
    IN_PROGRESS: { tone: 'progress',  label: 'In progress' },
  },
  work_order: {
    REQUESTED:   { tone: 'draft',     label: 'Requested' },
    OPEN:        { tone: 'progress',  label: 'Open' },
    IN_PROGRESS: { tone: 'treatment', label: 'In progress' },
    ON_HOLD:     { tone: 'attention', label: 'On hold' },
    DONE:        { tone: 'good',      label: 'Done' },
    CANCELLED:   { tone: 'gone',      label: 'Cancelled' },
  },
  // A business on the platform (tenants.status): the admin app's console and
  // tenant page. Trial = live but waiting to convert (staging); Suspended =
  // the platform must act to bring it back (attention); Archived = gone.
  tenant: {
    ACTIVE:    { tone: 'good',      label: 'Active' },
    TRIAL:     { tone: 'staging',   label: 'Trial' },
    SUSPENDED: { tone: 'attention', label: 'Suspended' },
    ARCHIVED:  { tone: 'gone',      label: 'Archived' },
  },
  // A goods-receipt line flag, U22 UF-07. A one-state flag derived from stored
  // fields (line_kind CHEMICAL and no stock_item_id), not a stored status.
  // Neutral grey: typing a chemical by name is offered on purpose.
  receipt_line: {
    NOT_IN_STOCK: { tone: 'draft', label: 'Not in stock' },
  },
  // A BigSecurity client site (security_sites.status, mig 20261113000000).
  security_site: {
    ONBOARDING: { tone: 'staging',   label: 'Onboarding' },
    ACTIVE:     { tone: 'good',      label: 'Active' },
    PAUSED:     { tone: 'attention', label: 'Paused' },
    ENDED:      { tone: 'gone',      label: 'Ended' },
  },
  // A blueberry force-cooling dolly (cooling_batches.status, UX-laws U04 UF-02).
  // ONE set of words for the board, History, the delivery page, the printed
  // register and the wall board. Six states, six tones; Released is `good`
  // (soft), never the button green.
  cooling_batch: {
    STAGING:    { tone: 'staging',  label: 'On the dolly' },
    COOLING:    { tone: 'progress', label: 'Force-cooling' },
    IN_HOLDING: { tone: 'stored',   label: 'In holding' },
    RELEASED:   { tone: 'good',     label: 'Released' },
    TIPPED:     { tone: 'gone',     label: 'Tipped' },
    CANCELLED:  { tone: 'bad',      label: 'Cancelled' },
  },
  // A harvest client's card (clientReadiness). NEEDS is one state: its words
  // (site, rate, both) go through StatusPill's `label`, the tone stays.
  harvest_client: {
    READY:        { tone: 'good',      label: 'Ready for the crews' },
    NEEDS:        { tone: 'attention', label: 'Needs setup' },
    UNKNOWN:      { tone: 'draft',     label: 'Checking' },
    UNIT_NOT_SET: { tone: 'exception', label: 'Crews\u2019 unit not set' },
  },
  // A maintenance asset (maintenance_assets.status).
  asset: {
    OPERATIONAL: { tone: 'good',      label: 'Operational' },
    DOWN:        { tone: 'bad',       label: 'Down' },
    MAINTENANCE: { tone: 'treatment', label: 'In maintenance' },
    RETIRED:     { tone: 'gone',      label: 'Retired' },
  },
  // One checklist step on a work order (ProcItemStatus).
  procedure_item: {
    PENDING: { tone: 'draft',     label: 'Pending' },
    DONE:    { tone: 'good',      label: 'Done' },
    SKIPPED: { tone: 'attention', label: 'Skipped' },
  },
  // A maintenance parts or service request to a supplier (SupplierRequestStatus); same object on both sides.
  supplier_request: {
    PENDING:   { tone: 'staging',  label: 'Pending' },
    ACCEPTED:  { tone: 'progress', label: 'Accepted' },
    DECLINED:  { tone: 'bad',      label: 'Declined' },
    CANCELLED: { tone: 'gone',     label: 'Cancelled' },
    RECEIVED:  { tone: 'good',     label: 'Received' },
  },
  // A supplier's delivery to a packhouse (supplier_delivery_status enum, lower-case keys).
  supplier_delivery: {
    pending:   { tone: 'staging', label: 'Pending' },
    accepted:  { tone: 'good',    label: 'Accepted' },
    rejected:  { tone: 'bad',     label: 'Rejected' },
    cancelled: { tone: 'gone',    label: 'Cancelled' },
  },
  // A non-conformance (non_conformances.status), FM10 UF-18. The SIZA area shows
  // it beside the action's NC number. RESOLVED waits for a manager's sign-off.
  non_conformance: {
    OPEN:        { tone: 'attention', label: 'Open' },
    IN_PROGRESS: { tone: 'progress',  label: 'In progress' },
    RESOLVED:    { tone: 'staging',   label: 'Resolved, awaiting sign-off' },
    CLOSED:      { tone: 'good',      label: 'Closed' },
  },
  // A pallet's last answer from eCert (ecert_trade_units.status, lower-case keys).
  // `not_sent` is the absence of a row. `pending` = eCert gave no explicit result
  // yet. `exists_elsewhere` = another system already registered it; we left it.
  ecert_trade_unit: {
    not_sent:         { tone: 'draft',     label: 'Not sent' },
    pending:          { tone: 'staging',   label: 'Pending' },
    accepted:         { tone: 'good',      label: 'Accepted' },
    rejected:         { tone: 'bad',       label: 'Rejected' },
    error:            { tone: 'attention', label: 'Error' },
    exists_elsewhere: { tone: 'stored',    label: 'Already in eCert' },
  },
  // A Config → Setup check result, UX-laws U27 UF-03: the banner and each
  // finding. Keys are the words' states: a clean pass, a warning, an error.
  setup_check: {
    passed:    { tone: 'good',      label: 'Passed' },
    attention: { tone: 'attention', label: 'Needs attention' },
    fix_now:   { tone: 'bad',       label: 'Fix now' },
  },
  // An agent's farm visit (VisitStatus).
  agent_visit: {
    DRAFT:        { tone: 'draft',    label: 'Draft' },
    SENT:         { tone: 'staging',  label: 'Sent' },
    ACKNOWLEDGED: { tone: 'progress', label: 'Acknowledged' },
    COMPLETED:    { tone: 'good',     label: 'Completed' },
    CANCELLED:    { tone: 'gone',     label: 'Cancelled' },
  },
}

/** The tone + words for one status. An unknown status is `draft` with a
 *  humanised label, never the raw value. */
export function statusTone(object: StatusObject, status: string | null | undefined): Entry {
  const hit = status ? STATUS_TONES[object][status] : undefined
  if (hit) return hit
  return { tone: 'draft', label: status ? enumWords(status) : 'Unknown' }
}

// Full class strings (not built from fragments) so Tailwind's scanner sees them.
const PILL: Record<Tone, string> = {
  draft:     'text-tone-draft bg-transparent ring-1 ring-inset ring-tone-draft-ring',
  progress:  'text-tone-progress bg-tone-progress-bg',
  stored:    'text-tone-stored bg-tone-stored-bg',
  staging:   'text-tone-staging bg-tone-staging-bg',
  treatment: 'text-tone-treatment bg-tone-treatment-bg',
  exception: 'text-tone-exception bg-tone-exception-bg',
  good:      'text-tone-good bg-tone-good-bg',
  attention: 'text-tone-attention bg-tone-attention-bg',
  bad:       'text-tone-bad bg-tone-bad-bg',
  gone:      'text-tone-gone bg-tone-gone-bg',
}
const DOT: Record<Tone, string> = {
  draft:     'bg-transparent ring-1 ring-inset ring-tone-draft-ring',
  progress:  'bg-tone-progress',
  stored:    'bg-tone-stored',
  staging:   'bg-tone-staging',
  treatment: 'bg-tone-treatment',
  exception: 'bg-tone-exception',
  good:      'bg-tone-good',
  attention: 'bg-tone-attention',
  bad:       'bg-tone-bad',
  gone:      'bg-tone-gone',
}
const EDGE: Record<Tone, string> = {
  draft:     'border-tone-draft-ring',
  progress:  'border-tone-progress',
  stored:    'border-tone-stored',
  staging:   'border-tone-staging',
  treatment: 'border-tone-treatment',
  exception: 'border-tone-exception',
  good:      'border-tone-good',
  attention: 'border-tone-attention',
  bad:       'border-tone-bad',
  gone:      'border-tone-gone',
}
const TEXT: Record<Tone, string> = {
  draft: 'text-tone-draft', progress: 'text-tone-progress', stored: 'text-tone-stored',
  staging: 'text-tone-staging', treatment: 'text-tone-treatment', exception: 'text-tone-exception',
  good: 'text-tone-good', attention: 'text-tone-attention', bad: 'text-tone-bad', gone: 'text-tone-gone',
}

/** Classes for the few non-pill uses of a tone. */
export function statusToneClasses(tone: Tone): { pill: string; dot: string; edge: string; text: string } {
  return { pill: PILL[tone], dot: DOT[tone], edge: EDGE[tone], text: TEXT[tone] }
}
